import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff } from 'lucide-react';
import { authFetch, readJSON } from '../lib/auth';
import CelebrationOverlay from '../components/CelebrationOverlay';
import BrandLogo from '../components/BrandLogo';

export default function Login() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [celebration, setCelebration] = useState(null);

  const playCelebrationThenNavigate = async (isRegister) => {
    if (!isRegister) { navigate('/'); return; }
    try {
      const res = await authFetch('/api/config/celebration');
      if (res.ok) {
        const cfg = await readJSON(res);
        if (cfg?.enabled !== false && cfg?.play_on_signup && cfg?.animation && cfg.animation !== 'off') {
          setCelebration(cfg.animation);
          setTimeout(() => navigate('/'), 2200);
          return;
        }
      }
    } catch { /* decorative */ }
    navigate('/');
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (mode === 'login') {
        await login({ email, password });
        navigate('/');
      } else {
        await register({ email, password, name });
        await playCelebrationThenNavigate(true);
      }
    } catch (err) {
      // Legacy Google-only users: auto-switch to Sign Up so they can claim their account.
      if (err?.code === 'needs_password_setup') {
        setMode('register');
        setError(err.message || 'Please set a password to claim your account.');
      } else {
        setError(err.message || 'Something went wrong');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-4 relative">
      {celebration && celebration !== 'off' && (
        <CelebrationOverlay animation={celebration} onDone={() => setCelebration('off')} />
      )}
      <div className="w-full max-w-md bg-slate-800/60 backdrop-blur-md border border-slate-700/60 rounded-2xl shadow-2xl p-8">
        <div className="flex flex-col items-center mb-6">
          <BrandLogo className="w-56 -mb-2" />
          <p className="text-sm text-slate-400 mt-1">
            {mode === 'login' ? 'Welcome back' : 'Create your account'}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === 'register' && (
            <input
              type="text"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="auth-name-input"
              className="w-full px-3 py-2 bg-slate-900/60 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
            />
          )}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="auth-email-input"
            className="w-full px-3 py-2 bg-slate-900/60 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
          />
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              data-testid="auth-password-input"
              className="w-full px-3 py-2 pr-10 bg-slate-900/60 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              data-testid="auth-show-password-toggle"
              className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-400 hover:text-amber-400 transition"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {error && (
            <div data-testid="auth-error" className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="auth-submit-btn"
            className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 disabled:opacity-60 text-white font-medium px-4 py-2.5 rounded-lg shadow transition"
          >
            {busy ? 'Working…' : mode === 'login' ? 'Sign in' : 'Create account'}
          </button>
        </form>

        <button
          type="button"
          onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
          data-testid="auth-toggle-mode"
          className="mt-4 text-sm text-slate-400 hover:text-amber-400 w-full text-center"
        >
          {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
        </button>

        {mode === 'login' && (
          <p className="mt-2 text-[11px] text-slate-500 text-center leading-relaxed">
            Previously signed in with Google? Click <span className="text-amber-400/80">Sign up</span> with the same email — your plan and credits will carry over.
          </p>
        )}

        <Link
          to="/"
          className="mt-3 text-xs text-slate-500 hover:text-slate-300 w-full text-center block"
        >
          Continue without account
        </Link>
      </div>
      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4 text-[11px] text-slate-600">
        <Link to="/privacy" className="hover:text-amber-400 transition">Privacy</Link>
        <Link to="/terms" className="hover:text-amber-400 transition">Terms</Link>
        <Link to="/refund" className="hover:text-amber-400 transition">Refund</Link>
        <Link to="/contact" className="hover:text-amber-400 transition">Contact</Link>
      </div>
    </div>
  );
}
